/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { AdminPlan } from '../types/su-plan/entities/AdminPlan';
import { PlanComputeCreateRequest } from '../types/su-plan/requests/PlanComputeCreateRequest';
import { PlanComputeUpdateRequest } from '../types/su-plan/requests/PlanComputeUpdateRequest';
import { PlanStorageCreateRequest } from '../types/su-plan/requests/PlanStorageCreateRequest';
import { PlanStorageUpdateRequest } from '../types/su-plan/requests/PlanStorageUpdateRequest';
import { PlanStorageUpdateResponse } from '../types/su-plan/requests/PlanStorageUpdateResponse';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class SUPlanApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchPlans = () => {
    return this.api.get<AdminPlan>('/administration/plan');
  };

  createPlanCompute = ({ data }: { data: PlanComputeCreateRequest }) => {
    return this.api.post<object>('/administration/plan/compute', data);
  };

  updatePlanCompute = ({ data, planId }: { planId: EntityId; data: PlanComputeUpdateRequest }) => {
    return this.api.patch(`/administration/plan/compute/${planId}`, data);
  };

  deletePlanCompute = ({ planId }: { planId: EntityId }) => {
    return this.api.del(`/administration/plan/compute/${planId}`);
  };

  createPlanStorage = ({ data }: { data: PlanStorageCreateRequest }) => {
    return this.api.post<PlanStorageCreateRequest>('/administration/plan/storage', data);
  };

  updatePlanStorage = ({ data, planId }: { planId: EntityId; data: PlanStorageUpdateRequest }) => {
    return this.api.patch<PlanStorageUpdateResponse>(`/administration/plan/storage/${planId}`, data);
  };

  deletePlanStorage = ({ planId }: { planId: EntityId }) => {
    return this.api.del(`/administration/plan/storage/${planId}`);
  };
}

export const SUPlanApiService = new SUPlanApiServiceClass();
